import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Calendar as CalendarIcon, Phone, Mail, Users, Loader2, Home, MapPin, AlertTriangle } from 'lucide-react';
import { format, startOfDay, isFriday, addDays, getDay } from 'date-fns';
import axios from 'axios';
import { toast } from 'sonner';
import { Calendar } from '@/components/ui/calendar';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import Navbar from '@/components/layout/Navbar';
import Footer from '@/components/layout/Footer';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Helper to get full image URL
const getImageUrl = (url) => {
  if (!url) return '';
  if (url.startsWith('http')) return url;
  if (url.startsWith('/api/')) return `${BACKEND_URL}${url}`;
  return url;
};

const countryCodes = [
  { value: '+1', label: '+1 (Canada/US)' },
  { value: '+44', label: '+44 (UK)' },
  { value: '+61', label: '+61 (Australia)' },
  { value: '+49', label: '+49 (Germany)' },
  { value: '+33', label: '+33 (France)' },
  { value: '+39', label: '+39 (Italy)' },
  { value: '+81', label: '+81 (Japan)' },
  { value: '+86', label: '+86 (China)' },
  { value: '+91', label: '+91 (India)' },
];

const howHeardOptions = [
  'Google Search',
  'Facebook',
  'Instagram',
  'Friend/Family Referral',
  'Returning Guest',
  'VRBO/HomeAway',
  'Airbnb',
  'Cottage Rental Website',
  'Magazine/Print',
  'Other',
];

export default function BookingPage() {
  const [availability, setAvailability] = useState([]);
  const [propertyImage, setPropertyImage] = useState(null);
  const [dateRange, setDateRange] = useState({ from: undefined, to: undefined });
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [pendingWarningOpen, setPendingWarningOpen] = useState(false);
  const [pendingWarningData, setPendingWarningData] = useState(null);
  const [formData, setFormData] = useState({
    num_occupants: '',
    age_range: '',
    first_name: '',
    last_name: '',
    home_address: '',
    city: '',
    province_state: '',
    postal_code: '',
    country: 'Canada',
    country_code: '+1',
    phone_home: '',
    phone_cell: '',
    fax: '',
    email: '',
    business_phone: '',
    business_ext: '',
    how_heard: '',
    questions_comments: '',
  });

  useEffect(() => {
    fetchAvailability();
    fetchPropertyImage();
  }, []);

  const fetchAvailability = async () => {
    try {
      const res = await axios.get(`${API}/availability`);
      setAvailability(res.data);
    } catch (error) {
      console.error('Error fetching availability:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchPropertyImage = async () => {
    try {
      // First try to fetch the dedicated booking page image from property settings
      const propRes = await axios.get(`${API}/property`);
      if (propRes.data?.booking_page_image) {
        setPropertyImage(propRes.data.booking_page_image);
        return;
      }
      // Fallback: fetch first gallery image
      const res = await axios.get(`${API}/gallery`);
      if (res.data && res.data.length > 0) {
        setPropertyImage(res.data[0].url);
      }
    } catch (error) {
      console.error('Error fetching property image:', error);
    }
  };

  const getDateStatus = (date) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    const found = availability.find(a => a.date === dateStr);
    return found?.status || 'available';
  };

  // Check if a Friday is a turnover day (checkout morning OR checkin afternoon, but NOT both)
  // If BOTH Thursday and Saturday are booked, it's a back-to-back = fully booked, NOT a turnover
  const isTurnoverDay = (date) => {
    if (!isFriday(date)) return false;
    
    const prevDay = addDays(date, -1);
    const prevDayStr = format(prevDay, 'yyyy-MM-dd');
    const prevDayStatus = availability.find(a => a.date === prevDayStr)?.status;
    
    const nextDay = addDays(date, 1);
    const nextDayStr = format(nextDay, 'yyyy-MM-dd');
    const nextDayStatus = availability.find(a => a.date === nextDayStr)?.status;
    
    const thursdayBooked = prevDayStatus === 'booked' || prevDayStatus === 'pending';
    const saturdayBooked = nextDayStatus === 'booked' || nextDayStatus === 'pending';
    
    // Back-to-back booking: Thursday booked AND Saturday booked = Friday is fully booked (no turnover)
    if (thursdayBooked && saturdayBooked) {
      return false;
    }
    
    // Checkout turnover: Thursday booked, Saturday NOT booked (guest leaving)
    if (thursdayBooked && !saturdayBooked) {
      return true;
    }
    
    // Checkin turnover: Thursday NOT booked, Saturday booked (guest arriving)
    if (!thursdayBooked && saturdayBooked) {
      return true;
    }
    
    return false;
  };

  // CHECKIN: Guest arriving Friday afternoon - show GREEN on top-left (AM available), BOOKED COLOR on bottom-right (PM booked)  
  // Returns 'booked', 'pending', or false
  const getCheckinTurnoverType = (date) => {
    if (!isFriday(date)) return false;
    
    const prevDay = addDays(date, -1);
    const prevDayStr = format(prevDay, 'yyyy-MM-dd');
    const prevDayStatus = availability.find(a => a.date === prevDayStr)?.status;
    
    const nextDay = addDays(date, 1);
    const nextDayStr = format(nextDay, 'yyyy-MM-dd');
    const nextDayEntry = availability.find(a => a.date === nextDayStr);
    const nextDayStatus = nextDayEntry?.status;
    
    // Checkin: Thursday was NOT booked (property was available), Saturday IS booked (guest staying)
    // This is the FIRST Friday of a booking - NOT a back-to-back
    const thursdayNotBooked = prevDayStatus !== 'booked' && prevDayStatus !== 'pending';
    const saturdayIsBooked = nextDayStatus === 'booked' || nextDayStatus === 'pending';
    
    // Only show checkin turnover if it's NOT a back-to-back (Thursday must be available)
    if (thursdayNotBooked && saturdayIsBooked) {
      return nextDayStatus; // Return 'booked' or 'pending'
    }
    return false;
  };

  // For backward compatibility
  const isCheckinTurnover = (date) => !!getCheckinTurnoverType(date);

  // CHECKOUT: Guest leaving Friday morning - show BOOKED COLOR on top-left, GREEN on bottom-right
  // Returns 'booked', 'pending', or false
  const getCheckoutTurnoverType = (date) => {
    if (!isFriday(date)) return false;
    
    const prevDay = addDays(date, -1);
    const prevDayStr = format(prevDay, 'yyyy-MM-dd');
    const prevDayEntry = availability.find(a => a.date === prevDayStr);
    const prevDayStatus = prevDayEntry?.status;
    
    const nextDay = addDays(date, 1);
    const nextDayStr = format(nextDay, 'yyyy-MM-dd');
    const nextDayStatus = availability.find(a => a.date === nextDayStr)?.status;
    
    const thursdayWasBooked = prevDayStatus === 'booked' || prevDayStatus === 'pending';
    const saturdayNotBooked = nextDayStatus !== 'booked' && nextDayStatus !== 'pending';
    
    if (thursdayWasBooked && saturdayNotBooked) {
      return prevDayStatus; // Return 'booked' or 'pending'
    }
    return false;
  };

  // For backward compatibility
  const isCheckoutTurnover = (date) => !!getCheckoutTurnoverType(date);

  // Check if a Friday is fully booked (part of a continuous booking)
  const isFridayFullyBooked = (date) => {
    if (!isFriday(date)) return false;
    const dateStr = format(date, 'yyyy-MM-dd');
    return availability.find(a => a.date === dateStr)?.status === 'booked';
  };

  const isDateDisabled = (date) => {
    const today = startOfDay(new Date());
    if (date < today) return true;
    
    // Only allow Fridays for selection
    if (!isFriday(date)) return true;
    
    // Get the status of this date
    const status = getDateStatus(date);
    
    // Turnover Fridays are ALWAYS selectable (checkout morning, checkin afternoon)
    if (isTurnoverDay(date)) return false;
    
    // Disable if this Friday is booked or pending (and not a turnover)
    if (status === 'booked' || status === 'pending') return true;
    
    // Available Fridays are selectable
    return false;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  // Check if selected dates are available before submitting
  const checkDateAvailability = async () => {
    try {
      const response = await axios.get(`${API}/availability/check-dates`, {
        params: {
          check_in: format(dateRange.from, 'yyyy-MM-dd'),
          check_out: format(dateRange.to, 'yyyy-MM-dd'),
        }
      });
      return response.data;
    } catch (error) {
      console.error('Error checking availability:', error);
      return null;
    }
  };

  // Submit the inquiry after all validations
  const submitInquiry = async () => {
    setSubmitting(true);
    try {
      await axios.post(`${API}/inquiries`, {
        ...formData,
        num_occupants: parseInt(formData.num_occupants),
        check_in_date: format(dateRange.from, 'yyyy-MM-dd'),
        check_out_date: format(dateRange.to, 'yyyy-MM-dd'),
      });

      toast.success('Inquiry submitted successfully! We\'ll contact you within 24-48 hours.');
      
      // Reset form
      setFormData({
        num_occupants: '',
        age_range: '',
        first_name: '',
        last_name: '',
        home_address: '',
        city: '',
        province_state: '',
        postal_code: '',
        country: 'Canada',
        country_code: '+1',
        phone_home: '',
        phone_cell: '',
        fax: '',
        email: '',
        business_phone: '',
        business_ext: '',
        how_heard: '',
        questions_comments: '',
      });
      setDateRange({ from: undefined, to: undefined });
      setPendingWarningOpen(false);
    } catch (error) {
      console.error('Error submitting inquiry:', error);
      toast.error('Failed to submit inquiry. Please try again or call us directly.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!dateRange?.from || !dateRange?.to) {
      toast.error('Please select check-in and check-out dates (Fridays only)');
      return;
    }

    // Validate both dates are Fridays
    if (!isFriday(dateRange.from) || !isFriday(dateRange.to)) {
      toast.error('Check-in and check-out must both be on Fridays');
      return;
    }

    // Validate minimum one week stay
    if (dateRange.to <= dateRange.from) {
      toast.error('Please select at least one week (Friday to Friday)');
      return;
    }

    // Validate required fields
    const requiredFields = [
      'num_occupants', 'age_range', 'first_name', 'last_name',
      'home_address', 'city', 'province_state', 'postal_code', 'country',
      'country_code', 'phone_home', 'phone_cell', 'email', 'how_heard'
    ];
    
    for (const field of requiredFields) {
      if (!formData[field]) {
        toast.error(`Please fill in all required fields`);
        return;
      }
    }

    // Check date availability before submitting
    setSubmitting(true);
    const availabilityCheck = await checkDateAvailability();
    
    if (availabilityCheck) {
      if (availabilityCheck.status === 'booked') {
        // Dates are booked - do not allow submission
        setSubmitting(false);
        toast.error('Sorry, the selected dates are already booked. Please choose different dates.');
        return;
      }
      
      if (availabilityCheck.status === 'pending') {
        // Dates have pending inquiries - show warning but allow submission
        setSubmitting(false);
        setPendingWarningData(availabilityCheck);
        setPendingWarningOpen(true);
        return;
      }
    }

    // Dates are available - proceed with submission
    await submitInquiry();
  };

  // Create sets of booked/pending date strings for fast lookup
  const bookedDateSet = new Set(
    availability.filter(a => a.status === 'booked').map(a => a.date)
  );
  
  const pendingDateSet = new Set(
    availability.filter(a => a.status === 'pending').map(a => a.date)
  );

  // Use matcher functions for modifiers
  const modifiers = {
    booked: (date) => {
      const dateStr = format(date, 'yyyy-MM-dd');
      return bookedDateSet.has(dateStr);
    },
    pending: (date) => {
      const dateStr = format(date, 'yyyy-MM-dd');
      return pendingDateSet.has(dateStr);
    },
    turnover_checkout_booked: (date) => getCheckoutTurnoverType(date) === 'booked',
    turnover_checkout_pending: (date) => getCheckoutTurnoverType(date) === 'pending',
    turnover_checkin_booked: (date) => getCheckinTurnoverType(date) === 'booked',
    turnover_checkin_pending: (date) => getCheckinTurnoverType(date) === 'pending',
    notFriday: (date) => !isFriday(date),
    availableFriday: (date) => {
      if (!isFriday(date)) return false;
      const dateStr = format(date, 'yyyy-MM-dd');
      const today = startOfDay(new Date());
      if (date < today) return false;
      return !bookedDateSet.has(dateStr) && !pendingDateSet.has(dateStr);
    },
  };

  const modifiersClassNames = {
    booked: 'rdp-day_booked',
    pending: 'rdp-day_pending',
    turnover_checkout_booked: 'rdp-day_turnover_checkout_booked',
    turnover_checkout_pending: 'rdp-day_turnover_checkout_pending',
    turnover_checkin_booked: 'rdp-day_turnover_checkin_booked',
    turnover_checkin_pending: 'rdp-day_turnover_checkin_pending',
    notFriday: 'rdp-day_not-friday',
    availableFriday: 'rdp-day_available-friday',
  };

  return (
    <div className="min-h-screen bg-[#F9F8F6]">
      <Navbar />
      
      {/* Hero */}
      <section className="pt-32 pb-12 container-padding">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="max-w-3xl"
        >
          <p className="text-caption text-[#D4AF37] mb-4">Reservation Inquiry</p>
          <h1 className="heading-display text-[#1C1C1C] mb-6">
            Book Your Stay
          </h1>
          <p className="text-body-lg text-[#66605B]">
            Select your dates and complete the inquiry form below. Our property manager will 
            contact you within 24-48 hours to confirm availability.
          </p>
        </motion.div>
      </section>

      {/* Property Summary */}
      <section className="container-padding pb-8">
        <div className="bg-[#F0EBE5] p-6 rounded-sm flex flex-col md:flex-row gap-6 items-center">
          {propertyImage && (
            <img 
              src={getImageUrl(propertyImage)}
              alt="Big Bear Estate"
              className="w-32 h-24 object-cover rounded"
            />
          )}
          <div>
            <h3 className="font-serif text-xl text-[#1C1C1C]">Big Bear</h3>
            <p className="text-[#66605B]">7 bedrooms • Sleeps 15 • 5 bathrooms (3 ensuites) • Lakefront with pool</p>
          </div>
        </div>
      </section>

      {/* Booking Form */}
      <section className="px-3 sm:px-6 md:px-12 lg:px-24 pb-20">
        <form onSubmit={handleSubmit}>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            
            {/* Left Column - Calendar */}
            <div className="lg:col-span-1">
              <div className="bg-white p-2 sm:p-6 rounded-sm shadow-sm sticky top-24">
                <h2 className="font-serif text-base sm:text-xl text-[#1C1C1C] mb-2 sm:mb-4 flex items-center gap-2">
                  <CalendarIcon className="w-5 h-5 text-[#1B4D3E]" />
                  Select Dates
                </h2>

                {/* Legend */}
                <div className="flex flex-wrap gap-2 sm:gap-3 mb-3 sm:mb-4 text-xs">
                  <div className="flex items-center gap-1">
                    <div className="w-3 h-3 bg-[#1B4D3E] rounded-sm" />
                    <span>Selected</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <div className="w-3 h-3 bg-[#DC2626] rounded-sm" />
                    <span>Booked</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <div className="w-3 h-3 bg-[#F59E0B] rounded-sm" />
                    <span>Pending</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <div className="w-3 h-3 rounded-sm turnover-legend-checkout" />
                    <span>Checkout AM</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <div className="w-3 h-3 rounded-sm turnover-legend-checkin" />
                    <span>Check-in PM</span>
                  </div>
                </div>
                <p className="text-xs text-[#66605B] mb-3 sm:mb-4">
                  <strong>Weekly rentals only:</strong> Friday check-in (4:30pm) to Friday check-out (11:00am)
                </p>

                <div className="calendar-large">
                  <Calendar
                    mode="range"
                    selected={dateRange}
                    onSelect={(range) => setDateRange(range || { from: undefined, to: undefined })}
                    disabled={isDateDisabled}
                    modifiers={modifiers}
                    modifiersClassNames={modifiersClassNames}
                    numberOfMonths={1}
                    className="rounded-sm border-0 w-full"
                    data-testid="booking-calendar"
                  />
                </div>

                {dateRange?.from && dateRange?.to && (
                  <div className="mt-4 p-3 bg-[#F0EBE5] rounded-sm text-sm">
                    <p className="font-medium text-[#1C1C1C]">Selected Week(s):</p>
                    <p className="text-[#66605B]">
                      Check-in: {format(dateRange.from, 'EEEE, MMM d, yyyy')} (4:30pm)
                    </p>
                    <p className="text-[#66605B]">
                      Check-out: {format(dateRange.to, 'EEEE, MMM d, yyyy')} (11:00am)
                    </p>
                  </div>
                )}

                {/* Contact Info */}
                <div className="mt-6 pt-6 border-t border-[#E2E0DC]">
                  <p className="text-sm text-[#66605B] mb-2">Questions? Call us:</p>
                  <a 
                    href="tel:416-543-4749" 
                    className="flex items-center gap-2 text-[#1B4D3E] font-medium"
                  >
                    <Phone className="w-4 h-4" />
                    416.543.4749
                  </a>
                </div>
              </div>
            </div>

            {/* Right Column - Form */}
            <div className="lg:col-span-2 space-y-8">
              
              {/* Dates & Occupants */}
              <div className="bg-white p-6 rounded-sm shadow-sm">
                <h3 className="font-serif text-lg text-[#1C1C1C] mb-4">Reservation Details</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="check_in">Start Date *</Label>
                    <Input
                      id="check_in"
                      value={dateRange?.from ? format(dateRange.from, 'yyyy-MM-dd') : ''}
                      readOnly
                      placeholder="Select from calendar"
                      className="bg-[#F9F8F6]"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="check_out">End Date *</Label>
                    <Input
                      id="check_out"
                      value={dateRange?.to ? format(dateRange.to, 'yyyy-MM-dd') : ''}
                      readOnly
                      placeholder="Select from calendar"
                      className="bg-[#F9F8F6]"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="num_occupants">No. of Occupants *</Label>
                    <Input
                      id="num_occupants"
                      name="num_occupants"
                      type="number"
                      min="1"
                      max="14"
                      value={formData.num_occupants}
                      onChange={handleInputChange}
                      placeholder="1-14"
                      required
                      data-testid="inquiry-occupants"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="age_range">Age Range of Occupants *</Label>
                    <Input
                      id="age_range"
                      name="age_range"
                      value={formData.age_range}
                      onChange={handleInputChange}
                      placeholder="e.g., Adults 30-50, Children 5-12"
                      required
                      data-testid="inquiry-age-range"
                    />
                  </div>
                </div>
              </div>

              {/* Personal Information */}
              <div className="bg-white p-6 rounded-sm shadow-sm">
                <h3 className="font-serif text-lg text-[#1C1C1C] mb-4">Personal Information</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="first_name">First Name *</Label>
                    <Input
                      id="first_name"
                      name="first_name"
                      value={formData.first_name}
                      onChange={handleInputChange}
                      required
                      data-testid="inquiry-first-name"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="last_name">Last Name *</Label>
                    <Input
                      id="last_name"
                      name="last_name"
                      value={formData.last_name}
                      onChange={handleInputChange}
                      required
                      data-testid="inquiry-last-name"
                    />
                  </div>
                </div>
              </div>

              {/* Address */}
              <div className="bg-white p-6 rounded-sm shadow-sm">
                <h3 className="font-serif text-lg text-[#1C1C1C] mb-4 flex items-center gap-2">
                  <Home className="w-5 h-5 text-[#1B4D3E]" />
                  Home Address
                </h3>
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="home_address">Street Address *</Label>
                    <Input
                      id="home_address"
                      name="home_address"
                      value={formData.home_address}
                      onChange={handleInputChange}
                      required
                      data-testid="inquiry-address"
                    />
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="city">City *</Label>
                      <Input
                        id="city"
                        name="city"
                        value={formData.city}
                        onChange={handleInputChange}
                        required
                        data-testid="inquiry-city"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="province_state">Province/State *</Label>
                      <Input
                        id="province_state"
                        name="province_state"
                        value={formData.province_state}
                        onChange={handleInputChange}
                        required
                        data-testid="inquiry-province"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="postal_code">Postal/ZIP Code *</Label>
                      <Input
                        id="postal_code"
                        name="postal_code"
                        value={formData.postal_code}
                        onChange={handleInputChange}
                        required
                        data-testid="inquiry-postal"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="country">Country *</Label>
                      <Input
                        id="country"
                        name="country"
                        value={formData.country}
                        onChange={handleInputChange}
                        required
                        data-testid="inquiry-country"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Contact Information */}
              <div className="bg-white p-6 rounded-sm shadow-sm">
                <h3 className="font-serif text-lg text-[#1C1C1C] mb-4 flex items-center gap-2">
                  <Phone className="w-5 h-5 text-[#1B4D3E]" />
                  Contact Information
                </h3>
                <div className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <Label>Country Code *</Label>
                      <Select 
                        value={formData.country_code} 
                        onValueChange={(v) => handleSelectChange('country_code', v)}
                      >
                        <SelectTrigger data-testid="inquiry-country-code">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {countryCodes.map(code => (
                            <SelectItem key={code.value} value={code.value}>
                              {code.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="phone_home">Phone (Home) *</Label>
                      <Input
                        id="phone_home"
                        name="phone_home"
                        type="tel"
                        value={formData.phone_home}
                        onChange={handleInputChange}
                        required
                        data-testid="inquiry-phone-home"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="phone_cell">Cell Phone *</Label>
                      <Input
                        id="phone_cell"
                        name="phone_cell"
                        type="tel"
                        value={formData.phone_cell}
                        onChange={handleInputChange}
                        required
                        data-testid="inquiry-phone-cell"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="fax">Fax (Optional)</Label>
                      <Input
                        id="fax"
                        name="fax"
                        type="tel"
                        value={formData.fax}
                        onChange={handleInputChange}
                        data-testid="inquiry-fax"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="email">Email *</Label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        required
                        data-testid="inquiry-email"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="space-y-2 md:col-span-2">
                      <Label htmlFor="business_phone">Business Phone (Optional)</Label>
                      <Input
                        id="business_phone"
                        name="business_phone"
                        type="tel"
                        value={formData.business_phone}
                        onChange={handleInputChange}
                        data-testid="inquiry-business-phone"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="business_ext">Ext. (Optional)</Label>
                      <Input
                        id="business_ext"
                        name="business_ext"
                        value={formData.business_ext}
                        onChange={handleInputChange}
                        data-testid="inquiry-business-ext"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Additional Information */}
              <div className="bg-white p-6 rounded-sm shadow-sm">
                <h3 className="font-serif text-lg text-[#1C1C1C] mb-4">Additional Information</h3>
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label>How Did You Hear About Us? *</Label>
                    <Select 
                      value={formData.how_heard} 
                      onValueChange={(v) => handleSelectChange('how_heard', v)}
                    >
                      <SelectTrigger data-testid="inquiry-how-heard">
                        <SelectValue placeholder="Please Select" />
                      </SelectTrigger>
                      <SelectContent>
                        {howHeardOptions.map(option => (
                          <SelectItem key={option} value={option}>
                            {option}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="questions_comments">Questions/Comments (Optional)</Label>
                    <Textarea
                      id="questions_comments"
                      name="questions_comments"
                      value={formData.questions_comments}
                      onChange={handleInputChange}
                      placeholder="Any special requests or questions..."
                      rows={4}
                      className="resize-none"
                      data-testid="inquiry-comments"
                    />
                  </div>
                </div>
              </div>

              {/* Submit */}
              <div className="bg-white p-6 rounded-sm shadow-sm">
                <Button
                  type="submit"
                  disabled={submitting || !dateRange?.from || !dateRange?.to}
                  className="w-full bg-[#1B4D3E] hover:bg-[#153E32] text-white py-6 uppercase tracking-widest text-xs font-bold"
                  data-testid="inquiry-submit"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Submitting Inquiry...
                    </>
                  ) : (
                    'Submit Reservation Inquiry'
                  )}
                </Button>
                <p className="text-xs text-[#66605B] text-center mt-4">
                  By submitting this form, you agree to be contacted regarding your rental inquiry. 
                  We typically respond within 24-48 hours.
                </p>
              </div>
            </div>
          </div>
        </form>
      </section>

      {/* Info Section */}
      <section className="bg-[#1B4D3E] text-white section-padding">
        <div className="container-padding">
          <div className="max-w-4xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-8 text-center">
            <div>
              <CalendarIcon className="w-10 h-10 mx-auto mb-4 text-[#D4AF37]" />
              <h3 className="font-serif text-lg mb-2">Weekly Rentals</h3>
              <p className="text-white/70 text-sm">
                Friday check-in at 4:30pm<br />
                Friday check-out at 11:00am
              </p>
            </div>
            <div>
              <Users className="w-10 h-10 mx-auto mb-4 text-[#D4AF37]" />
              <h3 className="font-serif text-lg mb-2">Max 15 Guests</h3>
              <p className="text-white/70 text-sm">
                7 bedrooms with 5 bathrooms<br />
                Perfect for groups & families
              </p>
            </div>
            <div>
              <Mail className="w-10 h-10 mx-auto mb-4 text-[#D4AF37]" />
              <h3 className="font-serif text-lg mb-2">Quick Response</h3>
              <p className="text-white/70 text-sm">
                We respond to all inquiries<br />
                within 24-48 hours
              </p>
            </div>
          </div>
        </div>
      </section>

      <Footer />

      {/* Pending Dates Warning Dialog */}
      <Dialog open={pendingWarningOpen} onOpenChange={setPendingWarningOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-600">
              <AlertTriangle className="w-5 h-5" />
              Dates Under Review
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-[#66605B]">
              {pendingWarningData?.pending_inquiry_count > 0 
                ? `There ${pendingWarningData.pending_inquiry_count === 1 ? 'is' : 'are'} already ${pendingWarningData.pending_inquiry_count} other inquir${pendingWarningData.pending_inquiry_count === 1 ? 'y' : 'ies'} for some or all of these dates.`
                : 'Some of the selected dates are currently being reviewed for other inquiries.'
              }
            </p>
            <p className="text-[#66605B]">
              You may still submit your inquiry, and we will contact you regarding availability.
            </p>
          </div>
          <DialogFooter className="flex gap-2 sm:gap-0">
            <Button
              variant="outline"
              onClick={() => setPendingWarningOpen(false)}
            >
              Select Different Dates
            </Button>
            <Button
              onClick={submitInquiry}
              disabled={submitting}
              className="bg-[#1B4D3E] hover:bg-[#153E32]"
            >
              {submitting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Submitting...
                </>
              ) : (
                'Submit Anyway'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
